import { observable, computed } from "mobx";
import config from "lib/config";
import {
  fetchCoworkerTeams,
  fetchResponsibleTeams,
  fetchReadOnlyTeams,
  fetchCoworkerUnits,
  fetchResponsibleUnits,
  fetchReadOnlyUnits,
} from "lib/endpoints/teams";
import { ALL_UNITS } from "Common/constants";
import { getString } from "lib/translation";
import * as TeamFunctionRights from "../../lib/constants/teamfunctionrights";
import ManagerUnitsStore from "../manager-units";

class TeamStore {
  @observable ready = false;
  @observable active = undefined;
  @observable list = [];
  @observable IsManager = true;
  @observable HrAccessOnlyIds = [];

  init() {
    this.reload();
  }

  @computed get selectionUnitId() {
    if (!this.active) {
      return null;
    }
    if (this.active.type === "unit") {
      return this.active.id;
    }
    if (this.active.type === "team") {
      return this.active.unitId;
    }
    return null;
  }

  async forceReload(filter, isManager, getReadOnlyUnits) {
    await this.reload(isManager, getReadOnlyUnits);
    this.updateTeamFilter(filter);
  }

  async reload(isManager = null, getReadOnlyUnits = false) {
    if (isManager == null)
      if (window.location.href.split("#")[1] === "/tm/coworker/engagement")
        this.IsManager = false;
      else this.IsManager = true;
    else this.IsManager = isManager;
    const fetchedTeams = await (this.IsManager
      ? fetchResponsibleTeams(config.personalId)
      : fetchCoworkerTeams(config.personalId));
    const fetchedUnits = await (this.IsManager
      ? fetchResponsibleUnits(config.personalId)
      : fetchCoworkerUnits(config.personalId));
    const readOnlyUnits = await (this.IsManager && getReadOnlyUnits
      ? fetchReadOnlyUnits()
      : []);
    const readOnlyTeams = await (this.IsManager && getReadOnlyUnits
      ? fetchReadOnlyTeams()
      : []);

    const readOnlyTeamObjects = readOnlyTeams.map((team) => {
      return { id: team.id, type: "team" };
    });

    const readOnlyUnitObjects = readOnlyUnits.map((unit) => {
      return { id: unit.id, type: "unit" };
    });

    this.HrAccessOnlyIds = readOnlyTeamObjects.concat(readOnlyUnitObjects);
    const units = fetchedUnits.concat(readOnlyUnits);
    const teams = fetchedTeams.concat(readOnlyTeams);

    const teamsList = teams
      .map((team) => {
        return {
          type: "team",
          id: Number(team.id),
          selectionQuery: "t=" + Number(team.id),
          title: team.title,
          permissions: team.permissions,
          isValid: true,
          unitId: team.unitId,
        };
      })
      // Remove duplicates
      .filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.id === value.id),
      );

    let unitsList = this.IsManager
      ? units.map((unit) => {
          return {
            type: "unit",
            id: Number(unit.id),
            selectionQuery: "u=" + Number(unit.id),
            title: unit.title,
            permissions: [],
            isValid: true,
          };
        })
      : units.map((unit) => {
          return {
            type: "unit",
            id: Number(unit.unitID),
            selectionQuery: "u=" + Number(unit.unitID),
            title: unit.unitName,
            permissions: [],
            isValid: true,
          };
        });
    // Remove duplicates
    unitsList = unitsList.filter(
      (value, index, self) =>
        index === self.findIndex((t) => t.id === value.id),
    );
    this.internalList = unitsList.concat(teamsList) || [];
    const shouldShowAllUnitsAlternative =
      this.shouldShowAllUnitsAlternative(unitsList);
    if (shouldShowAllUnitsAlternative) {
      this.internalList.unshift(this.getAllUnitsAlternative());
    }
    this.sortInternalList(shouldShowAllUnitsAlternative);
    this.updateTeamFilter(TeamFunctionRights.FILTER_SHOWNONE);
    this.ready = true;
  }

  getAllUnitsAlternative() {
    const translatedTitle = getString("TalentManagement", "allUnits");
    return {
      type: "unit",
      id: ALL_UNITS,
      title: translatedTitle === "" ? "All units" : translatedTitle,
      isValid: true,
      permissions: [],
      selectionQuery: "u=-2",
    };
  }

  sortInternalList(shouldAddAllUnitsAlternative) {
    if (this.internalList && this.internalList.length > 0) {
      this.internalList = this.internalList.filter(
        (item) => item.id !== ALL_UNITS,
      );

      const allUnitsItem = this.getAllUnitsAlternative();
      this.internalList.sort((a, b) => (a.title > b.title ? 1 : -1));
      if (shouldAddAllUnitsAlternative) {
        this.internalList.unshift(allUnitsItem);
      }
    }
  }

  shouldShowAllUnitsAlternative(unitsList) {
    return (
      this.IsManager &&
      unitsList.length > 1 &&
      (window.location.href.split("#")[1].startsWith("/tm/coworker") ||
        window.location.href.split("#")[1] === "/tm/manager/dashboard")
    );
  }

  async activateSelection(id, type) {
    const selectionIndex = this.list.findIndex(
      (el) => el.type === type && el.id === Number(id),
    );

    if (selectionIndex !== -1) {
      this.active = this.list[selectionIndex];
      if (type === "unit") ManagerUnitsStore.activateUnit(this.active.id);
    }
  }

  updateTeamFilter(newTeamFilter) {
    const newTeamFilterArray = Array.isArray(newTeamFilter)
      ? newTeamFilter
      : [newTeamFilter];
    if (
      JSON.stringify(this.teamFilter) === JSON.stringify(newTeamFilterArray)
    ) {
      return;
    }

    if (
      newTeamFilterArray.filter(
        (f) =>
          f !== TeamFunctionRights.FILTER_SHOWALL &&
          f !== TeamFunctionRights.FILTER_SHOWNONE &&
          f !== TeamFunctionRights.ID_COMPTALK &&
          f !== TeamFunctionRights.ID_ATTEST &&
          f !== TeamFunctionRights.ID_COURSESTATISTICS &&
          f !== TeamFunctionRights.ID_COMPETENCEMANAGEMENT &&
          f !== TeamFunctionRights.ID_REGISTERANDCANCEL &&
          f !== TeamFunctionRights.ID_SEARCHANDFILTER &&
          f !== TeamFunctionRights.ID_ENGAGE &&
          f !== TeamFunctionRights.ID_GOALSANDACTIVITIES,
      ).length > 0
    ) {
      throw new Error("Undefined/unknown filter for teams!");
    }

    if (
      newTeamFilterArray.indexOf(TeamFunctionRights.FILTER_SHOWALL) !== -1 ||
      newTeamFilterArray.indexOf(TeamFunctionRights.FILTER_SHOWNONE) !== -1
    ) {
      if (newTeamFilterArray.length !== 1) {
        throw new Error(
          "FILTER_SHOWALL and FILTER_SHOWNONE may not be combined with other values!",
        );
      }
    }
    this.teamFilter = newTeamFilterArray;
    if (this.teamFilter.indexOf(TeamFunctionRights.FILTER_SHOWALL) !== -1) {
      this.list = this.internalList;
    } else if (
      this.teamFilter.indexOf(TeamFunctionRights.FILTER_SHOWNONE) !== -1
    ) {
      this.list = this.internalList.filter((item) => item.type === "unit");
    } else {
      this.list =
        (!this.internalList && []) ||
        this.internalList.filter(
          (item) =>
            item.type !== "team" ||
            item.permissions.filter((p) => this.teamFilter.indexOf(p) !== -1)
              .length > 0,
        );
    }

    if (!this.isActiveSelectionValid()) {
      this.setFirstValidItemAsActive();
    }
  }

  isActiveSelectionValid() {
    if (this.active === undefined) {
      return false;
    }
    const activeExistsInList =
      this.list.findIndex(
        (el) => el.type === this.active.type && el.id === this.active.id,
      ) !== -1;
    const activeIsAllUnits = this.active?.id === ALL_UNITS;
    const activeIsAllUnitsAlternativeButTitlesMisMatch =
      activeIsAllUnits &&
      this.active.title !==
        this.list.find((unit) => unit.id === ALL_UNITS)?.title;

    if (activeIsAllUnitsAlternativeButTitlesMisMatch) {
      return false;
    }
    if (activeExistsInList) {
      return true;
    }

    return false;
  }

  setFirstValidItemAsActive() {
    this.active =
      this.list && this.list.length > 0
        ? this.list[0]
        : { title: "n/a", isValid: false };
  }

  getApiUrl() {
    // Return a string similar to "unit/1234" or "team/321" for the active selection. Can be used for api calls.
    return this.active.type + "/" + this.active.id;
  }
}

export default new TeamStore();
